import React from 'react';

import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  CERBERUS_STATS_FIELDS,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { formatNumberD3, getD3NumberFormat } from '@hcs/utils';

import { CERBERUS_STATS_TYPE } from '../../constants';
import {
  CerberusStatsFieldConfig,
  CerberusStatsProps,
  CerberusStatsTableCellProps,
  CerberusStatsTableHeaderCellProps,
} from '../../types';
import {
  getRentalLabel,
  logValueUnsupportedWarning,
} from '../../utils/cerberusStatsConfig.utils';

const D3_FORMAT_STR = getD3NumberFormat();

const FIELD = CERBERUS_STATS_FIELDS.listingsNewCount;
const FIELD_RENTAL = CERBERUS_STATS_FIELDS.listingsNewCountRental;
type FieldConfig = CerberusStatsFieldConfig<typeof FIELD>;
type FieldConfigRental = CerberusStatsFieldConfig<typeof FIELD_RENTAL>;
const label = 'Listings New Count';
const labelRental = getRentalLabel(label);
const labelShort = 'Listings New Count';
const labelRentalShort = getRentalLabel(labelShort);
const getValue: FieldConfig['getValue'] = (cerberusStats) => {
  if (!cerberusStats) {
    return undefined;
  }
  if (cerberusStats.type !== CERBERUS_STATS_TYPE.RentalListingStats) {
    return cerberusStats?.listingStats?.listingsNewCount;
  } else {
    logValueUnsupportedWarning(FIELD, cerberusStats);
    return undefined;
  }
};
const getValueRental: FieldConfigRental['getValue'] = (cerberusStats) => {
  if (!cerberusStats) {
    return undefined;
  }
  if (
    cerberusStats.type === CERBERUS_STATS_TYPE.Msa ||
    cerberusStats.type === CERBERUS_STATS_TYPE.RentalListingStats
  ) {
    return cerberusStats?.rentalListingStats?.listingsNewCount;
  } else {
    // we expect this to be potentially called for zip, since customized columns are the same for msa and zip
    if (cerberusStats.type !== CERBERUS_STATS_TYPE.Zip) {
      logValueUnsupportedWarning(FIELD_RENTAL, cerberusStats);
    }
    return undefined;
  }
};
const formatValue: FieldConfig['formatValue'] = (cerberusStats) =>
  formatNumberD3(getValue(cerberusStats));
const formatValueRental: FieldConfigRental['formatValue'] = (cerberusStats) =>
  formatNumberD3(getValueRental(cerberusStats));
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ cerberusStats }: CerberusStatsProps) => {
  return <>{formatValue(cerberusStats)}</>;
};
// eslint-disable-next-line react-refresh/only-export-components
const DisplayRental = ({ cerberusStats }: CerberusStatsProps) => {
  return <>{formatValueRental(cerberusStats)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: CerberusStatsTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// eslint-disable-next-line react-refresh/only-export-components
const HeaderCellRental = (props: CerberusStatsTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelRentalShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;
HeaderCellRental.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  cerberusStats,
  ...tableCellProps
}: CerberusStatsTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display cerberusStats={cerberusStats} />
    </TableCell>
  );
};
// eslint-disable-next-line react-refresh/only-export-components
const ContentCellRental = ({
  cerberusStats,
  ...tableCellProps
}: CerberusStatsTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <DisplayRental cerberusStats={cerberusStats} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;
ContentCellRental.defaultProps = defaultContentProps;

const definition =
  'Number of single family detached properties newly listed during the week ending on the date specified.';

const filterConfig: FieldConfig['filterConfig'] = {
  isPositive: true,
  formatWithCommas: true,
};

export const LISTINGS_NEW_COUNT_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  getValue,
  formatValue,
  HeaderCell,
  ContentCell,
  Display,
  d3FormatStr: D3_FORMAT_STR,
  definition,
  filterConfig,
};

export const LISTINGS_NEW_COUNT_RENTAL_CONFIG: FieldConfigRental = {
  field: FIELD_RENTAL,
  label: labelRental,
  labelShort: labelRentalShort,
  getValue: getValueRental,
  formatValue: formatValueRental,
  HeaderCell: HeaderCellRental,
  ContentCell: ContentCellRental,
  Display: DisplayRental,
  d3FormatStr: D3_FORMAT_STR,
  definition,
  filterConfig,
};
